import {
  DefectCatalogItem,
  toDefectMeasurementSlug,
  toDefectSlug,
  toDefectSurveyYearSlug,
} from '../contracts/availableLayers';
import type {
  DefectOverviewGridBand,
  DefectOverviewGridConfig,
} from '../defectOverlays/overviewGrid';
import {
  getDefectOverviewInitialZoom,
  selectDefectOverviewBand,
} from '../defectOverlays/overviewGrid';
import {
  CRACKING_RAG_COLOR_SCHEME,
  CRACKING_RAG_THRESHOLDS,
} from './crackingBands';

export const WINDSOR_CRACKMAP_TYPE_NAME = 'wfs_layers:windsor-mfv-crackmaps-2026-27';

/**
 * GeoServer WFS GetFeature endpoint for Windsor MFV cracking
 */
export const WINDSOR_CRACKMAP_WFS_URL =
  `https://geoserver.xais.co.uk/geoserver/wfs_layers/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${encodeURIComponent(WINDSOR_CRACKMAP_TYPE_NAME)}`;

/** GeoServer GWC WMTS endpoint used for crackmap overview (grid) tiles. */
export const CRACKMAP_WMTS_SERVICE_URL =
  'https://geoserver.xais.co.uk/geoserver/gwc/service/wmts';

export const CRACKMAP_WMTS_TILE_MATRIX_SET = 'EPSG:900913';

export const CRACKMAP_WMTS_FORMAT = 'image/png';

/** Street-level zoom where full crack vectors are fetched via WFS. */
export const CRACKMAP_INITIAL_ZOOM = 18;

export const CRACKMAP_STREET_ZOOM_EXIT = 17.5;

/** Duration (ms) for the in-place zoom when opening a crackmap below street level without overview tiles. */
export const CRACKMAP_STREET_ZOOM_DURATION_MS = 2800;

export const CRACKMAP_OVERVIEW_WMTS_STYLE = 'cracking';

let crackmapStreetLevelGateActive = false;

/**
 * Zoom ladder for Windsor crackmap overview grids.
 */
export const WINDSOR_CRACKMAP_OVERVIEW_WMTS: DefectOverviewGridConfig = {
  serviceUrl: CRACKMAP_WMTS_SERVICE_URL,
  tileMatrixSet: CRACKMAP_WMTS_TILE_MATRIX_SET,
  format: CRACKMAP_WMTS_FORMAT,
  bands: [
    {
      layerId: 'wfs_layers:windsor-mfv-crackmaps-100mgrid-2026-27',
      style: CRACKMAP_OVERVIEW_WMTS_STYLE,
      minZoom: 0,
      maxZoom: 13,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-crackmaps-50mgrid-2026-27',
      style: CRACKMAP_OVERVIEW_WMTS_STYLE,
      minZoom: 13,
      maxZoom: 15,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-crackmaps-10mgrid-2026-27',
      style: CRACKMAP_OVERVIEW_WMTS_STYLE,
      minZoom: 15,
      maxZoom: 17,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-crackmaps-5mgrid-2026-27',
      style: CRACKMAP_OVERVIEW_WMTS_STYLE,
      minZoom: 17,
      maxZoom: CRACKMAP_INITIAL_ZOOM,
    },
  ],
};


export function getCrackmapOverviewInitialZoom(
  config: DefectOverviewGridConfig,
): number {
  return getDefectOverviewInitialZoom(config, CRACKMAP_INITIAL_ZOOM);
}

export function isCrackmapStreetLevelZoom(zoom: number): boolean {
  return Number.isFinite(zoom) && zoom >= CRACKMAP_INITIAL_ZOOM;
}

export function resolveCrackmapStreetLevelGate(zoom: number): boolean {
  if (!Number.isFinite(zoom)) {
    crackmapStreetLevelGateActive = false;
    return false;
  }
  if (crackmapStreetLevelGateActive) {
    crackmapStreetLevelGateActive = zoom >= CRACKMAP_STREET_ZOOM_EXIT;
  } else {
    crackmapStreetLevelGateActive = zoom >= CRACKMAP_INITIAL_ZOOM;
  }
  return crackmapStreetLevelGateActive;
}

export function resetCrackmapStreetLevelGateForTests(): void {
  crackmapStreetLevelGateActive = false;
}

export function resolveCrackmapOverviewWmts(
  sourceLayerId: string | null | undefined,
): DefectOverviewGridConfig | null {
  const trimmed = sourceLayerId?.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed === WINDSOR_CRACKMAP_TYPE_NAME) {
    return WINDSOR_CRACKMAP_OVERVIEW_WMTS;
  }
  return null;
}

export function selectCrackmapOverviewBand(
  config: DefectOverviewGridConfig,
  zoom: number,
): DefectOverviewGridBand | null {
  return selectDefectOverviewBand(config, zoom, CRACKMAP_INITIAL_ZOOM);
}

export function isCrackmapCatalogEnabled(): boolean {
  return process.env.REACT_APP_CRACKMAP_WFS_ENABLED !== 'false';
}

export function getCrackmapCatalogDefects(): readonly DefectCatalogItem[] {
  if (!isCrackmapCatalogEnabled()) {
    return [];
  }

  return [
    {
      id: 'crackmap-windsor-mfv',
      slug: toDefectSlug('windsor-mfv-crackmaps'),
      surveyType: 'mfv',
      name: 'Cracking',
      code: 'CRCKMAP',
      type: 'Road Surface',
      presentation: 'crackmap',
      measurements: [
        {
          name: 'Cracking',
          slug: toDefectMeasurementSlug('crackmap-overlay'),
          code: 'CRCKO',
          description:
            '',
          parameter: 'crackmap',
          colorScheme: CRACKING_RAG_COLOR_SCHEME,
          thresholds: [...CRACKING_RAG_THRESHOLDS],
          surveyYears: [
            {
              id: 'windsor-2026-27',
              slug: toDefectSurveyYearSlug('windsor-2026-27'),
              surveyYear: '2026-27',
              startDate: '2026-04-01',
              endDate: '2027-03-31',
              sourceLayerId: WINDSOR_CRACKMAP_TYPE_NAME,
              vectorSourceUrl: WINDSOR_CRACKMAP_WFS_URL,
              lanes: ['all'],
            },
          ],
        },
      ],
    },
  ];
}
